import Button from "antd/es/button";
import Col from "antd/es/col";
import Input from "antd/es/input";
import Layout from "antd/es/layout";
import notification from "antd/es/notification";
import Row from "antd/es/row";
import React, { useCallback } from "react";
import { useDispatch, useSelector } from "react-redux";
import { RootState } from "src";
import AddOns from "src/components/utils/AddOns";
import FormInput from "src/components/utils/FormInput";
import MobileNavbar from "src/components/utils/MobileNavbar";
import { ChoiceGroup } from "src/components/utils/ChoiceGroup";
import { StyledRadioGroup } from "src/components/utils/RadioGroup";
import { getInTypeOptions, petOptions } from "src/constants/options";
import { useResizeObserver } from "src/hooks/useResizeObserver";
import { useMutation } from "urql";
import {
  setAddons,
  setAddress,
  setAppartment,
  setGetInType,
  setPets,
  setPriceNotice,
  setZip,
} from "../actions";
import { computeBookingPrice } from "../lib/price";
import { VerifyAddress } from "../graphql/mutations/address";
import { addressRefusal } from "../lib/address";
import { OTHER_PETS, petsList, petsValue, togglePet } from "../lib/pets";

const openNotificationWithIcon = (type, title, description) => {
  notification[type]({
    message: title,
    description,
  });
};

const StepThree = (props) => {
  const [loading, setLoading] = React.useState(false);
  const dispatch = useDispatch();
  const booking = useSelector((state: RootState) => state.bookingReducer);
  const {
    appartmentNo,
    address,
    addOns,
    getInType,
    pets,
    zip,
  } = booking;

  const width = useResizeObserver();
  const isMobile = width <= 500;

  const [, verifyAddress] = useMutation(VerifyAddress);

  /** The pets chosen so far. The store holds them as one string; see `lib/pets`. */
  const chosenPets = petsList(pets.type);

  const updateAddOns = useCallback(
    (addOns) => {
      dispatch(setAddons(addOns));
    },
    [dispatch]
  );


  const next = () => {
    setLoading(true);
    let errorTitle = "Invalid data";
    let errorMessage = "Please fill all the fields";
    /**
     * At least one answer about pets. `pets` itself is an object and so always
     * truthy — the old check passed on an unanswered question, which did not
     * matter while the choice was a radio group that started on "No Pets".
     */
    if (address.trim().length && getInType && chosenPets.length) {
      verifyAddress({ address }).then((result) => {
        const verified = result.data?.verifyAddress;
        const refusal = addressRefusal(verified);
        if (refusal) {
          setLoading(false);
          openNotificationWithIcon("error", refusal.title, refusal.description);
          return;
        }
        /**
         * The ZIP is kept as data, for the queries proximity pricing runs; the
         * address field keeps what the customer typed. The two are joined once,
         * where the booking is sent — see `withZip`.
         *
         * The address wins over the ZIP typed on step 1, since it is where the
         * cleaner goes. When it is in a different ZIP the area price may change,
         * so the total shown so far is kept for checkout to explain. When the
         * checker cannot pin a single ZIP, the one the customer typed stands.
         */
        const pinned = verified.zip;
        if (pinned && pinned !== zip) {
          dispatch(
            setPriceNotice({
              reason: "zip",
              wasTotalCents: computeBookingPrice(booking).totalCents,
              zip: pinned,
              fromZip: zip,
            })
          );
        }
        if (pinned) dispatch(setZip(pinned));
        props.changePage(4);
      });
    } else {
      setLoading(false);
      if (address.trim().length && getInType && !chosenPets.length) {
        errorTitle = "Tell us about pets";
        errorMessage =
          "Pick every pet you have, or “No Pets”. Some of our cleaners have allergies.";
      }
      openNotificationWithIcon("error", errorTitle, errorMessage);
    }
  };

  const prev = () => {
    props.changePage(2);
  };

  return (
    <Layout className="step-layout mobile-step-layout">
      {/* How often is asked on the date step now, where it changes the prices. */}
      <h1 className="semi-bold">Where would you like us to clean?</h1>
      <br />
      <Row gutter={16}>
        <Col lg={12} xs={24}>
          <span className="mt-2">Address</span> <br />
          <Input
            className="input"
            size="large"
            defaultValue={address}
            onChange={(event) => dispatch(setAddress(event.target.value))}
            placeholder="123 Madison St, New York"
          />
        </Col>
        <Col lg={12} xs={24}>
          <span className="mt-2">
            Appartment No. <small className="optional">(Optional)</small>
          </span>{" "}
          <br />
          <Input
            className="input"
            size="large"
            defaultValue={appartmentNo}
            onChange={(event) => dispatch(setAppartment(event.target.value))}
            placeholder="1023"
          />
        </Col>
      </Row>
      <br />
      <AddOns defaultValue={addOns.type} onChange={updateAddOns} />
      <br />
      <StyledRadioGroup
        size="large"
        defaultValue={getInType.type}
        value={getInType.type}
        id="getInType"
        name="getInType"
        label="How will we get in?"
        options={getInTypeOptions}
        style={
          isMobile
            ? {
                display: "grid",
                gridTemplateColumns: "repeat(2, 1fr)",
              }
            : {}
        }
        onChange={(e) => {
          dispatch(setGetInType(e.target.value));
        }}
      />
      {getInType.type === "Hidden Key" || getInType.type === "Other" ? (
        <FormInput
          id="getInTypeOther"
          name="getInTypeOther"
          placeholder={
            getInType.type === "Hidden Key"
              ? "Where is it?"
              : "How do we get in?"
          }
          defaultValue={getInType.text}
          onChange={(e) =>
            dispatch(setGetInType(getInType.type, e.target.value))
          }
        />
      ) : null}
      <br />
      <ChoiceGroup
        size="large"
        id="pets"
        label="Do you have any pets? Pick all that apply."
        value={chosenPets}
        options={petOptions}
        style={
          isMobile
            ? {
                display: "grid",
                gridTemplateColumns: "repeat(2, 1fr)",
              }
            : {}
        }
        onChange={(pet) =>
          dispatch(setPets(petsValue(togglePet(chosenPets, pet)), pets.text))
        }
      />
      {chosenPets.includes(OTHER_PETS) ? (
        <FormInput
          size="large"
          id="petsOther"
          name="petsOther"
          defaultValue={pets.text}
          placeholder="What types of pets? Some of our cleaners have pet allergies."
          onChange={(e) => dispatch(setPets(pets.type, e.target.value))}
        />
      ) : null}
      <br />
      {isMobile ? (
        <MobileNavbar loading={loading} nextAction={next} prevAction={prev} />
      ) : (
        <div className="button-group center">
          <Button
            onClick={() => props.changePage(2)}
            type="primary"
            className="next-button back-button"
          >
            Back
          </Button>
          <Button onClick={() => next()} type="primary" className="next-button">
            {loading ? "Please wait..." : "Next"}
          </Button>
        </div>
      )}
      <br />
    </Layout>
  );
};

export default StepThree;
